import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { PremiumCombobox } from '@/components/ui/premium-combobox';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import {
  Download, Search, ArrowUpDown, ArrowUp, ArrowDown, ExternalLink,
  MessageSquare, Headset, Hash, Activity, Tag, Flag, X, Plus, UserRoundPlus, Wrench,
  Sparkles, UserRound, Clock, CheckCircle2, HelpCircle, Layers, Merge,
} from 'lucide-react';
import { DataPagination, PAGE_SIZE_OPTIONS } from '@/components/DataPagination';
import { NewTicketDialog } from '@/components/NewTicketDialog';
import { AiUnansweredDialog } from '@/components/AiUnansweredDialog';
import { fetchSupportAiEnabled } from '@/lib/workerApi';
import {
  setTicketStatus,
  TICKET_STATUSES, TICKET_TYPES, TICKET_PRIORITIES, TICKET_TAGS,
  STATUS_LABELS, TYPE_LABELS, PRIORITY_LABELS, TAG_LABELS,
  STATUS_CLASSES, TYPE_CLASSES, SOURCE_CLASSES, PRIORITY_CLASSES, TAG_CLASSES,
  ticketTitle, QUEUE_LABELS, ticketQueue, getCreatorFullNames, ticketSubtitle,
  applyAiQueue, AI_QUEUE_LABELS, AI_HANDOFF_REASON_LABELS, AI_MAX_REPLIES_PER_TICKET,
  type TicketRow, type TicketStatus, type TicketPriority, type TicketTag, type AiQueue,
} from '@/lib/support';

/** BMC Assistant queue presets, in display order. 'all' = no AI filter. */
const AI_QUEUES: { value: AiQueue; icon: typeof Sparkles }[] = [
  { value: 'all', icon: Layers },
  { value: 'needs_human', icon: UserRound },
  { value: 'ai_handling', icon: Sparkles },
  { value: 'ai_replied_24h', icon: Clock },
  { value: 'ai_follow_up', icon: Flag },
  { value: 'ai_resolved', icon: CheckCircle2 },
];

// The support queue. Every row is a ticket in `user_feedback`; clicking one
// opens /ticket-chat/:id, where the agent works the same conversation the
// customer sees. Status/priority/type/source and the styling for each live in
// @/lib/support so the queue and the detail page can never disagree.
//
// The queue's organizing idea is `awaiting_reply` — no thread yet, or the
// customer spoke last — because that is the only column that answers "what do
// I work on next". It's a computed column on user_feedback_with_user, so it
// filters and counts server-side.

type SortConfig = { key: string; direction: 'asc' | 'desc' } | null;

const SORTABLE_COLUMNS: Record<string, boolean> = {
  ticket_number: true,
  type: true,
  source: true,
  message: true,
  email: true,
  created_at: true,
  last_activity_at: true,
  status: true,
  priority: true,
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Narrows a query to one source tab. 'coach' is its own tab even though
 *  those rows are source = 'creator' (see ticketQueue), so the Creator tab
 *  has to exclude them explicitly. */
function applyQueue(q: any, queue: string) {
  if (queue === 'coach') return q.eq('is_coach', true);
  if (queue === 'creator') return q.eq('source', 'creator').eq('is_coach', false);
  if (queue === 'brand') return q.eq('source', 'brand');
  return q;
}

/** Who opened the ticket — the axis the source tabs (creator/brand/coach) don't
 *  cover. 'support' is user_feedback.opened_by_admin. */
const ORIGINS = ['customer', 'support'] as const;
const ORIGIN_LABELS: Record<(typeof ORIGINS)[number], string> = {
  customer: 'Opened by customer',
  support: 'Opened by support',
};

// ============================================================================
// Sticky filters — mirrored into the URL so a "Back" navigation from the
// chat page (which just goes `navigate(-1)` / `navigate('/feedback')`) lands
// an agent back on the exact queue view they left, not a reset "All" tab.
// Same pattern as CreatorPayments.tsx's readFilters/writeFilters.
// ============================================================================

const SOURCE_KEYS = ['all', 'creator', 'brand'];

const DEFAULTS = {
  source: 'all',
  type: 'all',
  status: 'all',
  priority: 'all',
  tag: 'all',
  origin: 'all',
  awaiting: false,
  q: '',
  page: 1,
  size: 25,
  sort: 'last_activity_at',
  dir: 'desc' as 'asc' | 'desc',
};

/** ?size= snapped to a size the dropdown actually offers — see
 *  CreatorPayments.tsx's readPageSize for why a raw URL value can't be
 *  trusted as-is. */
function readPageSize(raw: string | null): number {
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) return DEFAULTS.size;
  const offered = [...PAGE_SIZE_OPTIONS].sort((a, b) => a - b);
  const snapped = offered.filter((size) => size <= n).pop();
  return snapped ?? offered[0] ?? DEFAULTS.size;
}

function readFilters(sp: URLSearchParams) {
  const one = <T,>(key: string, allowed: ReadonlyArray<T>, fallback: T): T => {
    const raw = sp.get(key);
    return allowed.includes(raw as T) ? (raw as T) : fallback;
  };
  const int = (key: string, fallback: number, min: number) => {
    const n = Number(sp.get(key));
    return Number.isInteger(n) && n >= min ? n : fallback;
  };
  return {
    source:   one('source', SOURCE_KEYS, DEFAULTS.source),
    type:     one('type', ['all', ...TICKET_TYPES], DEFAULTS.type),
    status:   one('status', ['all', ...TICKET_STATUSES], DEFAULTS.status),
    priority: one('priority', ['all', ...TICKET_PRIORITIES], DEFAULTS.priority),
    tag:      one('tag', ['all', ...TICKET_TAGS], DEFAULTS.tag),
    origin:   one('origin', ['all', ...ORIGINS], DEFAULTS.origin),
    awaiting: sp.get('awaiting') === '1',
    merged:   sp.get('merged') === '1',
    q:        sp.get('q') ?? DEFAULTS.q,
    page:     int('page', DEFAULTS.page, 1),
    size:     readPageSize(sp.get('size')),
    sort:     one('sort', Object.keys(SORTABLE_COLUMNS), DEFAULTS.sort),
    dir:      one<'asc' | 'desc'>('dir', ['asc', 'desc'], DEFAULTS.dir),
  };
}

function writeFilters(f: ReturnType<typeof readFilters>): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.source   !== DEFAULTS.source)   sp.set('source', f.source);
  if (f.type     !== DEFAULTS.type)     sp.set('type', f.type);
  if (f.status   !== DEFAULTS.status)   sp.set('status', f.status);
  if (f.priority !== DEFAULTS.priority) sp.set('priority', f.priority);
  if (f.tag      !== DEFAULTS.tag)      sp.set('tag', f.tag);
  if (f.origin   !== DEFAULTS.origin)   sp.set('origin', f.origin);
  if (f.awaiting)                       sp.set('awaiting', '1');
  if (f.merged)                         sp.set('merged', '1');
  if (f.q        !== DEFAULTS.q)        sp.set('q', f.q);
  if (f.page     !== DEFAULTS.page)     sp.set('page', String(f.page));
  if (f.size     !== DEFAULTS.size)     sp.set('size', String(f.size));
  if (f.sort     !== DEFAULTS.sort)     sp.set('sort', f.sort);
  if (f.dir      !== DEFAULTS.dir)      sp.set('dir', f.dir);
  return sp;
}

/** An applied filter, shown as a chip that clears itself. Read-only badges
 *  made you go back up to the control that set them; this removes that trip. */
function FilterChip({ label, value, onClear }: { label: string; value: string; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 py-1 pl-2.5 pr-1 text-xs shadow-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{value}</span>
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear ${label} filter`}
        className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-primary/20 hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

export default function Feedback() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Read ONCE, on mount. Coming back from a ticket chat is a remount, and this
  // is where the filters an agent set up before clicking in come back from.
  // After mount the state below owns the values and the effect further down
  // mirrors them out — reading continuously instead would fight every setState.
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = useRef(readFilters(searchParams)).current;

  const [sourceFilter, setSourceFilter] = useState<string>(initial.source);
  const [typeFilter, setTypeFilter] = useState<string>(initial.type);
  const [statusFilter, setStatusFilter] = useState<string>(initial.status);
  const [priorityFilter, setPriorityFilter] = useState<string>(initial.priority);
  // A tag ('technical' today) is a separate axis from `type` — it answers WHO
  // should work the ticket, not what it's about, so the dev team can filter
  // to exactly their queue without a `type` value doing double duty.
  const [tagFilter, setTagFilter] = useState<string>(initial.tag);
  // Who started the ticket. Since support can open one itself, "the customer
  // asked us" and "we reached out" are two different piles of work, and the
  // queue was showing them as one.
  const [originFilter, setOriginFilter] = useState<string>(initial.origin);
  const [awaitingOnly, setAwaitingOnly] = useState(initial.awaiting);
  // Tickets merged into another one are closed duplicates; their messages
  // live in the primary. Hidden from the queue unless asked for (or a search
  // is looking for one by number/text). When on, lists ONLY merged tickets.
  const [showMerged, setShowMerged] = useState(initial.merged);
  const [searchInput, setSearchInput] = useState(initial.q);
  // Seeded from the same value rather than '' so a restored search filters on
  // the first render instead of showing every ticket for the debounce's 300ms.
  const [search, setSearch] = useState(initial.q);
  const [currentPage, setCurrentPage] = useState(initial.page);
  const [pageSize, setPageSize] = useState(initial.size);
  const [isExporting, setIsExporting] = useState(false);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [unansweredOpen, setUnansweredOpen] = useState(false);
  const [aiQueue, setAiQueue] = useState<AiQueue>('all');
  // A queue sorts by what moved most recently, not by what arrived first — a
  // three-week-old ticket the customer just replied to belongs at the top.
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: initial.sort, direction: initial.dir });

  // Debounce search. Skips its own first run: `search` is already seeded from
  // the URL, so the only thing the mount pass would do is setCurrentPage(1) —
  // throwing away the page number just restored.
  const searchSettled = useRef(false);
  useEffect(() => {
    if (!searchSettled.current) { searchSettled.current = true; return; }
    const t = setTimeout(() => { setSearch(searchInput); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Mirror the filter set back into the URL. replace: true, so a session of
  // tweaking filters leaves ONE history entry and the chat page's Back goes to
  // wherever the agent came from, not through every intermediate filter state.
  useEffect(() => {
    const next = writeFilters({
      source: sourceFilter, type: typeFilter, status: statusFilter, priority: priorityFilter,
      tag: tagFilter, origin: originFilter, awaiting: awaitingOnly, merged: showMerged, q: search,
      page: currentPage, size: pageSize,
      sort: sortConfig?.key ?? DEFAULTS.sort, dir: sortConfig?.direction ?? DEFAULTS.dir,
    }).toString();
    if (next !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [sourceFilter, typeFilter, statusFilter, priorityFilter, tagFilter, originFilter, awaitingOnly, showMerged,
      search, currentPage, pageSize, sortConfig, searchParams, setSearchParams]);

  const applyFilters = (query: any, { includeSource = true }: { includeSource?: boolean } = {}) => {
    let q = query;
    if (includeSource) q = applyQueue(q, sourceFilter);
    if (typeFilter !== 'all') q = q.eq('type', typeFilter);
    if (statusFilter !== 'all') q = q.eq('status', statusFilter);
    if (priorityFilter !== 'all') q = q.eq('priority', priorityFilter);
    if (tagFilter !== 'all') q = q.contains('tags', [tagFilter]);
    if (originFilter !== 'all') q = q.eq('opened_by_admin', originFilter === 'support');
    if (awaitingOnly) q = q.eq('awaiting_reply', true);
    q = applyAiQueue(q, aiQueue);

    const term = search.trim();
    if (showMerged) q = q.not('merged_into_ticket_id', 'is', null);
    else if (!term) q = q.is('merged_into_ticket_id', null);
    if (term) {
      const filters = [
        `message.ilike.%${term}%`,
        `subject.ilike.%${term}%`,
        `email.ilike.%${term}%`,
        `internal_note.ilike.%${term}%`,
        `display_name.ilike.%${term}%`,
      ];
      // A bare number is almost always someone pasting a ticket reference.
      if (/^\d+$/.test(term)) filters.push(`ticket_number.eq.${term}`);
      if (UUID_PATTERN.test(term)) filters.push(`user_id.eq.${term}`);
      q = q.or(filters.join(','));
    }
    return q;
  };

  const applySort = (query: any) => {
    if (!sortConfig) return query.order('last_activity_at', { ascending: false, nullsFirst: false });
    return query.order(sortConfig.key, {
      ascending: sortConfig.direction === 'asc',
      nullsFirst: false,
    });
  };

  const { data: ticketData, isLoading } = useQuery({
    queryKey: ['feedback', sourceFilter, typeFilter, statusFilter, priorityFilter, tagFilter, originFilter, awaitingOnly, showMerged, aiQueue, search, currentPage, pageSize, sortConfig?.key, sortConfig?.direction],
    queryFn: async () => {
      let q = supabase.from('user_feedback_with_user').select('*', { count: 'exact' });
      q = applyFilters(q);
      q = applySort(q);
      const { data, count, error } = await q.range((currentPage - 1) * pageSize, currentPage * pageSize - 1);
      if (error) throw error;
      return { data: (data ?? []) as TicketRow[], total: count ?? 0 };
    },
  });

  const tickets = ticketData?.data ?? [];
  const totalTickets = ticketData?.total ?? 0;

  // Creator full names for the rows on screen, one query per page. Brand
  // tickets are skipped — their requester is a brand, not a person.
  const creatorIds = Array.from(new Set(
    tickets.filter((t) => t.source !== 'brand' && t.user_id).map((t) => t.user_id as string),
  )).sort();
  const { data: fullNames } = useQuery({
    queryKey: ['feedback-creator-full-names', creatorIds],
    queryFn: () => getCreatorFullNames(creatorIds),
    enabled: creatorIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const totalPages = Math.ceil(totalTickets / pageSize);

  // Per-source tab counts, respecting every filter EXCEPT source, so each tab
  // shows exactly what selecting it would list. Head-only — no rows fetched.
  const { data: sourceCounts } = useQuery({
    queryKey: ['feedback-source-counts', typeFilter, statusFilter, priorityFilter, tagFilter, originFilter, awaitingOnly, showMerged, aiQueue, search],
    queryFn: async () => {
      const countFor = (queue: string) => {
        let q = supabase.from('user_feedback_with_user').select('id', { count: 'exact', head: true });
        q = applyFilters(q, { includeSource: false });
        return applyQueue(q, queue);
      };
      const [all, creator, brand, coach] = await Promise.all([
        countFor('all'), countFor('creator'), countFor('brand'), countFor('coach'),
      ]);
      return { all: all.count ?? 0, creator: creator.count ?? 0, brand: brand.count ?? 0, coach: coach.count ?? 0 };
    },
  });

  // BMC Assistant queue counts — global (no other filter), so each chip reads
  // as "how much is in that pile right now". Head-only, polled like the
  // awaiting count.
  const { data: aiQueueCounts } = useQuery({
    queryKey: ['feedback-ai-queue-counts'],
    queryFn: async () => {
      const queues = AI_QUEUES.filter((x) => x.value !== 'all').map((x) => x.value);
      const results = await Promise.all(queues.map((queue) =>
        applyAiQueue(supabase.from('user_feedback_with_user').select('id', { count: 'exact', head: true }).is('merged_into_ticket_id', null), queue),
      ));
      return Object.fromEntries(queues.map((queue, i) => [queue, (results[i] as { count: number | null }).count ?? 0])) as Partial<Record<AiQueue, number>>;
    },
    refetchInterval: 60000,
  });

  // SUPPORT_AI_ENABLED on the worker. Off (or not yet known) hides the AI
  // queue bar and the AI handoffs button; nothing else on the page changes.
  const { data: aiEnabled = false } = useQuery({
    queryKey: ['support-ai-enabled'],
    queryFn: fetchSupportAiEnabled,
    staleTime: 5 * 60 * 1000,
  });

  // The one headline number: tickets that need a human right now.
  const { data: awaitingCount } = useQuery({
    queryKey: ['support-awaiting-count'],
    queryFn: async () => {
      const { count } = await supabase
        .from('user_feedback_with_user')
        .select('id', { count: 'exact', head: true })
        .eq('awaiting_reply', true);
      return count ?? 0;
    },
    refetchInterval: 60000,
  });

  // Status changes go through the RPC (which also posts the customer-visible
  // system message and writes the event), never a bare table update.
  const statusMutation = useMutation({
    mutationFn: ({ id, from, to }: { id: string; from: TicketStatus; to: TicketStatus }) =>
      setTicketStatus(id, from, to),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-source-counts'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-ai-queue-counts'] });
      queryClient.invalidateQueries({ queryKey: ['support-awaiting-count'] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Brand tickets get a real chat thread too now (Brand-Dashboard's
  // Support.tsx + supportTickets.ts, calling the same support_* RPCs with
  // source: 'brand') — /ticket-chat/:id already works identically for both
  // sources (ensureConversation/postMessage never branch on it), so a brand
  // row is just as navigable as a creator row.
  const openTicket = useCallback((row: TicketRow) => {
    navigate(`/ticket-chat/${row.id}`);
  }, [navigate]);

  // Poll for arrivals every 60s and offer a jump straight into the chat.
  const lastCheckRef = useRef<string>(new Date().toISOString());
  useEffect(() => {
    const interval = setInterval(async () => {
      const { data } = await supabase
        .from('user_feedback_with_user')
        .select('*')
        .gt('created_at', lastCheckRef.current)
        .order('created_at', { ascending: false })
        .limit(10);

      if (data && data.length > 0) {
        lastCheckRef.current = new Date().toISOString();
        queryClient.invalidateQueries({ queryKey: ['feedback'] });
        queryClient.invalidateQueries({ queryKey: ['support-awaiting-count'] });
        // A ticket support opened itself didn't arrive "from" anyone — the
        // agent who created it is already looking at it.
        (data as TicketRow[]).filter((item) => !item.opened_by_admin).forEach((item) => {
          toast(`New ${TYPE_LABELS[item.type as keyof typeof TYPE_LABELS] || item.type} from ${item.display_name || item.email || 'unknown'}${item.is_coach ? ' via BMC Coach' : ''}`, {
            action: { label: 'Open chat', onClick: () => navigate(`/ticket-chat/${item.id}`) },
          });
        });
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [queryClient, navigate]);

  const truncate = (s: string, len = 70) => (s && s.length > len ? `${s.slice(0, len)}…` : s);
  const isSortable = (key: string) => SORTABLE_COLUMNS[key] === true;

  const handleSort = (key: string) => {
    if (!isSortable(key)) return;
    setCurrentPage(1);
    setSortConfig((cur) =>
      !cur || cur.key !== key || cur.direction === 'desc'
        ? { key, direction: 'asc' }
        : { key, direction: 'desc' });
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    return sortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const batchSize = 1000;
      let from = 0;
      let rows: any[] = [];
      while (true) {
        let q = supabase.from('user_feedback_with_user').select('*');
        q = applyFilters(q);
        q = applySort(q);
        const { data: batch, error } = await q.range(from, from + batchSize - 1);
        if (error) throw error;
        if (!batch || batch.length === 0) break;
        rows = rows.concat(batch);
        if (batch.length < batchSize) break;
        from += batchSize;
      }

      if (rows.length === 0) {
        toast.error('No matching tickets available to export');
        return;
      }

      const columns: string[] = Array.from(
        rows.reduce((keys: Set<string>, row) => {
          Object.keys(row).forEach((k) => keys.add(k));
          return keys;
        }, new Set<string>()),
      );
      const escape = (v: any) => {
        const s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
        return `"${s.replace(/"/g, '""')}"`;
      };
      const csv = [columns.join(','), ...rows.map((r) => columns.map((c) => escape(r[c])).join(','))].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `support-tickets-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} tickets`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to export tickets');
    } finally {
      setIsExporting(false);
    }
  };

  const filtersActive = typeFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all' || tagFilter !== 'all' || originFilter !== 'all' || awaitingOnly || showMerged || !!search.trim();

  const clearAllFilters = () => {
    setCurrentPage(1);
    setStatusFilter('all');
    setTypeFilter('all');
    setPriorityFilter('all');
    setTagFilter('all');
    setOriginFilter('all');
    setAwaitingOnly(false);
    setShowMerged(false);
    setAiQueue('all');
    setSearchInput('');
    setSearch('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {/* Wrapping and shrinking are load-bearing here, not cosmetic: Layout's
          <main> is overflow-x-hidden, so anything this row pushes past the
          right edge is CLIPPED rather than scrollable — which is how the
          New Ticket button went missing at laptop widths. The search box must
          be allowed to shrink and the buttons must never be the thing that
          overflows. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-[0_8px_20px_-8px_hsl(var(--primary)/0.8)]">
            <Headset className="h-4.5 w-4.5" />
          </span>
          <div>
            <h1 className="text-xl font-bold tracking-tight sm:text-2xl">Support Tickets</h1>
            <p className="text-xs text-muted-foreground">
              {totalTickets} {totalTickets === 1 ? 'ticket' : 'tickets'} in this view
            </p>
          </div>
          {!!awaitingCount && awaitingCount > 0 && (
            <button type="button" onClick={() => { setCurrentPage(1); setAwaitingOnly(true); }}>
              <Badge variant="destructive" className="gap-1 cursor-pointer hover:opacity-90">
                <MessageSquare className="h-3 w-3" />
                {awaitingCount} awaiting reply
              </Badge>
            </button>
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-2 sm:flex-1 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <div className="relative w-full sm:w-96 sm:min-w-[11rem] sm:flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search ticket #, subject, message, email…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9"
            />
          </div>
          <Button variant="outline" className="shrink-0 whitespace-nowrap" onClick={handleExport} disabled={isExporting || totalTickets === 0}>
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
          {aiEnabled && (
            <Button variant="outline" className="shrink-0 whitespace-nowrap" onClick={() => setUnansweredOpen(true)}>
              <HelpCircle className="mr-2 h-4 w-4" /> AI handoffs
            </Button>
          )}
          <Button className="shrink-0 whitespace-nowrap" onClick={() => setNewTicketOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Ticket
          </Button>
        </div>
      </div>

      {aiEnabled && <AiUnansweredDialog open={unansweredOpen} onOpenChange={setUnansweredOpen} />}

      <NewTicketDialog
        open={newTicketOpen}
        onOpenChange={setNewTicketOpen}
        onCreated={(t) => {
          queryClient.invalidateQueries({ queryKey: ['feedback'] });
          queryClient.invalidateQueries({ queryKey: ['feedback-source-counts'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-ai-queue-counts'] });
          queryClient.invalidateQueries({ queryKey: ['support-awaiting-count'] });
          navigate(`/ticket-chat/${t.ticketId}`);
        }}
      />

      {/* Filter rail: source tabs and the narrowing dropdowns share a single
          row. Status/type/priority are dropdowns rather than 17
          permanently-rendered chips. */}
      <Card className="p-4">
        {/* BMC Assistant queues — who's holding each ticket right now. */}
        {aiEnabled && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5 border-b pb-3">
          {AI_QUEUES.map(({ value, icon: Icon }) => {
            const active = aiQueue === value;
            const count = value === 'all' ? undefined : aiQueueCounts?.[value];
            return (
              <button
                key={value}
                type="button"
                onClick={() => { setCurrentPage(1); setAiQueue(value); }}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  active
                    ? value === 'needs_human'
                      ? 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300'
                      : 'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-300'
                    : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {AI_QUEUE_LABELS[value]}
                {typeof count === 'number' && (
                  <span className={`rounded-full px-1.5 tabular-nums ${
                    value === 'needs_human' && count > 0 ? 'bg-rose-500 text-white' : 'bg-muted text-muted-foreground'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs value={sourceFilter} onValueChange={(v) => { setCurrentPage(1); setSourceFilter(v); }}>
            <TabsList>
              {[
                { value: 'all', label: 'All', count: sourceCounts?.all },
                { value: 'creator', label: 'Creator', count: sourceCounts?.creator },
                { value: 'brand', label: 'Brand', count: sourceCounts?.brand },
                { value: 'coach', label: 'BMC Coach', count: sourceCounts?.coach },
              ].map(({ value, label, count }) => (
                <TabsTrigger key={value} value={value}>
                  {label}
                  {typeof count === 'number' && (
                    <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
                      {count}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className={
                awaitingOnly
                  ? 'gap-1.5 border-primary/50 bg-primary/10 text-primary ring-1 ring-primary/15 hover:bg-primary/15 hover:text-primary'
                  : 'gap-1.5'
              }
              onClick={() => { setCurrentPage(1); setAwaitingOnly(!awaitingOnly); }}
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Needs reply
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={
                showMerged
                  ? 'gap-1.5 border-primary/50 bg-primary/10 text-primary ring-1 ring-primary/15 hover:bg-primary/15 hover:text-primary'
                  : 'gap-1.5'
              }
              onClick={() => { setCurrentPage(1); setShowMerged(!showMerged); }}
              title="Show only tickets that were merged into another ticket (hidden from the queue by default)"
            >
              <Merge className="h-3.5 w-3.5" />
              Show merged
            </Button>
            <PremiumCombobox
              value={statusFilter}
              onValueChange={(v) => { setCurrentPage(1); setStatusFilter(v); }}
              icon={<Activity className="h-3.5 w-3.5 text-muted-foreground" />}
              searchPlaceholder="Search statuses…"
              className="w-[190px]"
              triggerClassName={statusFilter !== 'all' ? 'border-primary/50 ring-1 ring-primary/15' : undefined}
              options={[
                { value: 'all', label: 'All statuses' },
                ...TICKET_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
              ]}
            />
            <PremiumCombobox
              value={typeFilter}
              onValueChange={(v) => { setCurrentPage(1); setTypeFilter(v); }}
              icon={<Tag className="h-3.5 w-3.5 text-muted-foreground" />}
              searchPlaceholder="Search types…"
              className="w-[160px]"
              triggerClassName={typeFilter !== 'all' ? 'border-primary/50 ring-1 ring-primary/15' : undefined}
              options={[
                { value: 'all', label: 'All types' },
                ...TICKET_TYPES.map((t) => ({ value: t, label: TYPE_LABELS[t] })),
              ]}
            />
            <PremiumCombobox
              value={priorityFilter}
              onValueChange={(v) => { setCurrentPage(1); setPriorityFilter(v); }}
              icon={<Flag className="h-3.5 w-3.5 text-muted-foreground" />}
              searchPlaceholder="Search priorities…"
              className="w-[160px]"
              triggerClassName={priorityFilter !== 'all' ? 'border-primary/50 ring-1 ring-primary/15' : undefined}
              options={[
                { value: 'all', label: 'All priorities' },
                ...TICKET_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABELS[p] })),
              ]}
            />
            <PremiumCombobox
              value={tagFilter}
              onValueChange={(v) => { setCurrentPage(1); setTagFilter(v); }}
              icon={<Wrench className="h-3.5 w-3.5 text-muted-foreground" />}
              searchPlaceholder="Search tags…"
              className="w-[160px]"
              triggerClassName={tagFilter !== 'all' ? 'border-primary/50 ring-1 ring-primary/15' : undefined}
              options={[
                { value: 'all', label: 'All tags' },
                ...TICKET_TAGS.map((t) => ({ value: t, label: TAG_LABELS[t] })),
              ]}
            />
            {/* Origin filter ("Anyone opened") disabled for now — unused, and
                keeping it off the filter rail is what lets everything else
                fit on one line without a horizontal scrollbar.
            <PremiumCombobox
              value={originFilter}
              onValueChange={(v) => { setCurrentPage(1); setOriginFilter(v); }}
              icon={<UserRoundPlus className="h-3.5 w-3.5 text-muted-foreground" />}
              searchPlaceholder="Search origins…"
              className="w-[170px]"
              triggerClassName={originFilter !== 'all' ? 'border-primary/50 ring-1 ring-primary/15' : undefined}
              options={[
                { value: 'all', label: 'Anyone opened' },
                ...ORIGINS.map((o) => ({ value: o, label: ORIGIN_LABELS[o] })),
              ]}
            />
            */}
          </div>
        </div>

        {filtersActive && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border/60 pt-3">
            {awaitingOnly && (
              <FilterChip label="Queue" value="Needs reply" onClear={() => { setCurrentPage(1); setAwaitingOnly(false); }} />
            )}
            {showMerged && (
              <FilterChip label="Showing" value="Only merged tickets" onClear={() => { setCurrentPage(1); setShowMerged(false); }} />
            )}
            {statusFilter !== 'all' && (
              <FilterChip label="Status" value={STATUS_LABELS[statusFilter as TicketStatus]} onClear={() => { setCurrentPage(1); setStatusFilter('all'); }} />
            )}
            {typeFilter !== 'all' && (
              <FilterChip label="Type" value={TYPE_LABELS[typeFilter as keyof typeof TYPE_LABELS]} onClear={() => { setCurrentPage(1); setTypeFilter('all'); }} />
            )}
            {priorityFilter !== 'all' && (
              <FilterChip label="Priority" value={PRIORITY_LABELS[priorityFilter as TicketPriority]} onClear={() => { setCurrentPage(1); setPriorityFilter('all'); }} />
            )}
            {tagFilter !== 'all' && (
              <FilterChip label="Tag" value={TAG_LABELS[tagFilter as TicketTag]} onClear={() => { setCurrentPage(1); setTagFilter('all'); }} />
            )}
            {/* Paired with the disabled origin filter above.
            {originFilter !== 'all' && (
              <FilterChip label="Opened by" value={originFilter === 'support' ? 'Support' : 'Customer'} onClear={() => { setCurrentPage(1); setOriginFilter('all'); }} />
            )}
            */}
            {search.trim() && (
              <FilterChip label="Search" value={search.trim()} onClear={() => { setSearchInput(''); setSearch(''); setCurrentPage(1); }} />
            )}
            <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs text-muted-foreground" onClick={clearAllFilters}>
              Clear all
            </Button>
          </div>
        )}
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">
                <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort('ticket_number')}>
                  <span>Ticket</span>{getSortIcon('ticket_number')}
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort('message')}>
                  <span>Subject</span>{getSortIcon('message')}
                </Button>
              </TableHead>
              <TableHead className="w-52">
                <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort('email')}>
                  <span>Requester</span>{getSortIcon('email')}
                </Button>
              </TableHead>
              <TableHead className="w-24">
                <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort('type')}>
                  <span>Type</span>{getSortIcon('type')}
                </Button>
              </TableHead>
              <TableHead className="w-24">
                <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort('priority')}>
                  <span>Priority</span>{getSortIcon('priority')}
                </Button>
              </TableHead>
              <TableHead className="w-28">
                <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort('created_at')}>
                  <span>Created</span>{getSortIcon('created_at')}
                </Button>
              </TableHead>
              <TableHead className="w-36">
                <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort('last_activity_at')}>
                  <span>Activity</span>{getSortIcon('last_activity_at')}
                </Button>
              </TableHead>
              <TableHead className="w-36">
                <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort('status')}>
                  <span>Status</span>{getSortIcon('status')}
                </Button>
              </TableHead>
              <TableHead className="w-20">Open</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Skeleton rows rather than a single "Loading…" cell: the table
              // keeps its shape, so nothing jumps when the data lands.
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {Array.from({ length: 9 }).map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              tickets.map((t) => (
                <TableRow
                  key={t.id}
                  className={`cursor-pointer ${t.awaiting_reply ? 'bg-rose-50/40 hover:bg-rose-50/70 dark:bg-rose-950/10' : ''}`}
                  onClick={() => openTicket(t)}
                >
                  <TableCell>
                    <span className="inline-flex items-center gap-0.5 font-mono text-xs font-semibold tabular-nums text-muted-foreground">
                      <Hash className="h-3 w-3" />{t.ticket_number ?? '—'}
                    </span>
                  </TableCell>

                  <TableCell className="max-w-sm">
                    <div className="flex items-center gap-2">
                      {/* An unanswered ticket is the whole point of the queue —
                          flagged inline so it survives any sort order. */}
                      {t.awaiting_reply && (
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" title="Awaiting reply" />
                      )}
                      {/* Only the support-opened ones are badged. Marking both
                          origins would put a label on every single row; the eye
                          finds the exception far faster than it tells two
                          competing labels apart. The "Opened by" filter answers
                          it definitively when the badge isn't enough. */}
                      {t.merged_into_ticket_id && (
                        <span
                          title={`Merged into #${t.merged_into_ticket_number ?? '—'}${t.merged_by_name ? ` by ${t.merged_by_name}` : ''}`}
                          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-1.5 py-0 text-[10px] font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                        >
                          <Merge className="h-2.5 w-2.5" /> Merged → #{t.merged_into_ticket_number ?? '—'}
                        </span>
                      )}
                      {t.merged_ticket_count > 0 && (
                        <span
                          title={`${t.merged_ticket_count} duplicate ticket${t.merged_ticket_count === 1 ? '' : 's'} merged into this one`}
                          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-300 bg-slate-50 px-1.5 py-0 text-[10px] font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                        >
                          <Merge className="h-2.5 w-2.5" /> +{t.merged_ticket_count}
                        </span>
                      )}
                      {t.opened_by_admin && (
                        <span
                          title="Support opened this ticket — the customer did not send it"
                          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0 text-[10px] font-semibold text-indigo-700 dark:border-indigo-900 dark:bg-indigo-950/50 dark:text-indigo-300"
                        >
                          <Headset className="h-2.5 w-2.5" /> Support
                        </span>
                      )}
                      {/* Static read-only badge — the tag itself is only ever
                          set from the Manage dropdown on the ticket detail
                          page now, never from the queue row. Rendered only
                          when the ticket actually carries the tag, so an
                          untagged ticket shows nothing here. */}
                      {/* BMC Assistant state — who is holding this ticket. */}
                      {t.handled_by === 'ai' && t.ai_status === 'active' && (
                        <span
                          title={`BMC Assistant is answering — ${t.ai_reply_count}/${AI_MAX_REPLIES_PER_TICKET} AI replies used`}
                          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0 text-[10px] font-semibold text-violet-700 dark:border-violet-900 dark:bg-violet-950/50 dark:text-violet-300"
                        >
                          <Sparkles className="h-2.5 w-2.5" /> AI · {t.ai_reply_count}/{AI_MAX_REPLIES_PER_TICKET}
                        </span>
                      )}
                      {t.ai_awaiting_human && (
                        <span
                          title={t.ai_handoff_reason ? `Handed off: ${AI_HANDOFF_REASON_LABELS[t.ai_handoff_reason]}` : 'Handed off by BMC Assistant'}
                          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-1.5 py-0 text-[10px] font-semibold text-rose-700 dark:border-rose-900 dark:bg-rose-950/50 dark:text-rose-300"
                        >
                          <UserRound className="h-2.5 w-2.5" /> Needs human
                        </span>
                      )}
                      {t.ai_status === 'resolved' && (
                        <span
                          title="The customer confirmed BMC Assistant solved it"
                          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0 text-[10px] font-semibold text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300"
                        >
                          <Sparkles className="h-2.5 w-2.5" /> AI resolved
                        </span>
                      )}
                      {t.tags?.includes('technical') && (
                        <span
                          title="Tagged Technical — the dev team filters the queue on this"
                          className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0 text-[10px] font-semibold ${TAG_CLASSES.technical}`}
                        >
                          <Wrench className="h-2.5 w-2.5" /> {TAG_LABELS.technical}
                        </span>
                      )}
                      <span className="truncate text-sm font-medium" title={t.message}>{truncate(ticketTitle(t))}</span>
                      {ticketSubtitle(t) && (
                        <span
                          title="Subject"
                          className="inline-flex max-w-[12rem] shrink-0 items-center truncate rounded-full border border-border bg-muted/60 px-1.5 py-0 text-[10px] font-medium text-muted-foreground"
                        >
                          {ticketSubtitle(t)}
                        </span>
                      )}
                      {t.admin_unread_count > 0 && (
                        <Badge variant="destructive" className="h-4 shrink-0 px-1.5 text-[10px]">
                          {t.admin_unread_count}
                        </Badge>
                      )}
                    </div>
                    {t.last_message_preview ? (
                      <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
                        {t.last_message_sender_type === 'agent' && (
                          t.handled_by === 'ai'
                            ? <Sparkles className="h-3 w-3 shrink-0 text-violet-500" />
                            : <Headset className="h-3 w-3 shrink-0" />
                        )}
                        {truncate(t.last_message_preview, 80)}
                      </p>
                    ) : t.opened_by_admin && t.message_count === 0 ? (
                      // The state unique to a support-opened ticket: it exists,
                      // but nobody has said anything in it yet.
                      <p className="mt-0.5 truncate text-xs italic text-muted-foreground">
                        No messages yet — send the first one to start it
                      </p>
                    ) : null}
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-col">
                      <span className="truncate text-sm font-medium">
                        {t.display_name || <span className="text-muted-foreground">—</span>}
                      </span>
                      {t.source !== 'brand' && t.user_id && fullNames?.[t.user_id] && fullNames[t.user_id] !== t.display_name && (
                        <span className="truncate text-xs text-muted-foreground">{fullNames[t.user_id]}</span>
                      )}
                      {/* For a brand ticket, t.email is the SUBMITTING TEAMMATE's own
                          login email (support_open_ticket snapshots auth.users.email
                          once at creation) — an agency user managing several brands
                          shows the identical address under every one of them. Prefer
                          the brand's own contact_email/delivery_email (see migration
                          20260921000004) when the ticket is attributed to a brand;
                          fall back to the submitter's email for creator tickets,
                          unattributed legacy brand tickets, or a brand that never set
                          a contact email. */}
                      <a
                        href={`mailto:${t.brand_contact_email || t.email}`}
                        className="truncate text-xs text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t.brand_contact_email || t.email || '—'}
                      </a>
                      <span className={`mt-1 inline-flex w-fit items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold ${SOURCE_CLASSES[ticketQueue(t)]}`}>
                        {QUEUE_LABELS[ticketQueue(t)]}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${TYPE_CLASSES[t.type] || TYPE_CLASSES.general}`}>
                      {TYPE_LABELS[t.type as keyof typeof TYPE_LABELS] || t.type}
                    </span>
                  </TableCell>

                  <TableCell>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${PRIORITY_CLASSES[t.priority]}`}>
                      {PRIORITY_LABELS[t.priority]}
                    </span>
                  </TableCell>

                  <TableCell className="text-xs text-muted-foreground">
                    {t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}
                  </TableCell>

                  <TableCell className="text-xs text-muted-foreground">
                    {t.last_activity_at
                      ? formatDistanceToNow(new Date(t.last_activity_at), { addSuffix: true })
                      : t.created_at
                        ? formatDistanceToNow(new Date(t.created_at), { addSuffix: true })
                        : '—'}
                    {t.assigned_admin_email && (
                      <div className="mt-0.5 truncate text-[10px]">→ {t.assigned_admin_email}</div>
                    )}
                  </TableCell>

                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={t.status}
                      onValueChange={(v) => statusMutation.mutate({ id: t.id, from: t.status, to: v as TicketStatus })}
                      disabled={!!t.merged_into_ticket_id}
                    >
                      <SelectTrigger className={`h-8 w-32 border text-xs font-semibold ${STATUS_CLASSES[t.status]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TICKET_STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>

                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button variant="outline" size="sm" className="h-8" onClick={() => openTicket(t)}>
                      <ExternalLink className="mr-1 h-3.5 w-3.5" /> Chat
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
            {!isLoading && tickets.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="py-12 text-center text-muted-foreground">
                  {filtersActive || sourceFilter !== 'all' ? 'No matching tickets found' : 'No tickets yet'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {tickets.length > 0 && (
          <DataPagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalTickets}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => { setCurrentPage(1); setPageSize(newSize); }}
          />
        )}
      </Card>
    </div>
  );
}
